// @ts-nocheck
import { put, call, takeLatest, takeEvery, select } from "redux-saga/effects";
import axios from "axios";
import Constants from "expo-constants";
import type { Generator } from "redux-saga/effects";
import { i18n } from "../config/i18n";
import {
  doListProducts,
  doFetchProducts,
  doFetchConfig,
  invalidToken,
  selectAggregator,
} from "../actions";
import type { Locale } from "../types";
import { getCurrentLocale, processError } from "../utils";
import { getFullURL } from "../api";
import { preferredLocaleSelector } from "../selectors";
import {
  MAKE_API_CALL,
  STARTUP_PROCESS,
  LOCALE_CHANGE,
  SET_SHOULD_UPGRADE,
} from "../constants/actionTypes";

const API_TOKEN = Constants.expoConfig?.extra?.apiToken;

export default function* generalSaga() {
  yield takeEvery(MAKE_API_CALL, makeApiCall);
  yield takeLatest(STARTUP_PROCESS, startupProcess);
  yield takeLatest(LOCALE_CHANGE, handleLocaleChange);
}

export function getHeaders(locale?: Locale) {
  return {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${API_TOKEN}`,
      "Accept-Language": locale || getCurrentLocale(),
    },
  };
}

// Initialize headers with current locale
const { headers } = getHeaders(getCurrentLocale());
if (headers) {
  axios.defaults.headers = headers;
}

export function getMethod(method) {
  switch (method) {
    case "GET":
      return (requestUrl, data, config) => axios.get(requestUrl, config);
    case "POST":
      return axios.post;
    case "PUT":
      return axios.put;
    case "DELETE":
      return axios.delete;
    default:
      return axios.get;
  }
}

export function addAdditionalHeaders(reqHeaders: any, featureFlags: any) {
  if (!featureFlags) {
    return reqHeaders;
  }
  if (!reqHeaders) {
    return {
      headers: featureFlags,
    };
  }
  return {
    headers: {
      ...reqHeaders.headers,
      ...featureFlags,
    },
  };
}

export function setLocale(locale: Locale) {
  if (!locale) return;
  try {
    const { headers: defaultHeader } = getHeaders(locale);
    if (defaultHeader) {
      axios.defaults.headers = defaultHeader;
      i18n.locale = locale;
      // setDateLocale(locale);
    }
  } catch (error) {
    console.error("Error setting locale:", error);
  }
}

function* handleLocaleChange({ payload: locale }: { payload: Locale }) {
  if (!locale) return;
  setLocale(locale);
  yield put(doListProducts());
  yield put(doFetchProducts(true));
}

function* syncPreferredLocale() {
  try {
    const preferredLocale = yield select(preferredLocaleSelector);
    if (preferredLocale) {
      setLocale(preferredLocale);
    }
  } catch (error) {
    console.error("Error syncing preferred locale:", error);
  }
}

export function* makeApiCall(
  urlObj,
  payload: any,
  apiHeaders?: { headers: any },
  processErrors = true,
) {
  const method = getMethod(urlObj.method);
  const endPoint = getFullURL(urlObj.url, urlObj.prefix, urlObj.apiVersion);
  const reqHeaders = addAdditionalHeaders(apiHeaders, urlObj.featureFlags);
  const handleUnauthorized =
    urlObj.handleUnauthorized !== undefined ? urlObj.handleUnauthorized : true;

  let errorResponse = null;
  try {
    let apiOutput = null;
    if (urlObj.method === "DELETE") {
      apiOutput = yield call(method, endPoint, reqHeaders);
    } else {
      apiOutput = yield call(method, endPoint, payload, reqHeaders);
    }
    if (apiOutput && apiOutput.status >= 200 && apiOutput.status < 300) {
      return apiOutput.data;
    }
  } catch (error) {
    errorResponse = error;
    const unauthorizedStatus = error?.response?.status === 401;
    if (handleUnauthorized && unauthorizedStatus) {
      yield put(invalidToken());
    }
  }

  if (processErrors) {
    const processedError = processError(errorResponse);
    if (
      (processedError.error || processedError.errors) &&
      processedError.message === "417"
    ) {
      yield put({
        type: SET_SHOULD_UPGRADE,
        payload: true,
      });
    }
    return processedError;
  }

  return errorResponse;
}

export function* startupProcess(): Generator {
  // This needs to be first so that the api calls use the appropriate locale

  yield call(syncPreferredLocale);

  yield put(doFetchProducts(true));
  yield put(doFetchConfig());

  yield put(selectAggregator());
}
